import { useEffect, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AttentionStrip } from './AttentionStrip'
import { useRegisterHeader, useShell } from './shell'

export interface PageHeaderProps {
  title: ReactNode
  /** One line, only when the title alone does not say what the page is for. */
  description?: ReactNode
  /** The page's one main action, on the right. */
  actions?: ReactNode
  /** The pages above this one, outermost first: "앱 › Korio › …". */
  parent?: Crumb | Crumb[]
  /** This page's short name at the end of the trail, so the trail never ends
   *  on a bare chevron ("앱 › Korio"). Plain text, `aria-current="page"`. */
  crumb?: string
  /** The filter row, under the title. */
  children?: ReactNode
  /** The browser tab's name when `title` is not plain text. */
  docTitle?: string
  /** `false` leaves the attention strip out — for a page that already lists
   *  the same things itself (오늘). */
  attention?: boolean
  /** Strip items this page leaves out because it is where they lead (알림
   *  leaves out "안 읽은 알림"); the other items stay. */
  attentionOmit?: string[]
  className?: string
}

export interface Crumb {
  to: string
  label: string
}

const APP_NAME = 'Adfusion 콘솔'

/** "달력 · Adfusion 콘솔" in the tab, so a row of console tabs can be told
 *  apart. */
function useDocumentTitle(name: string | undefined) {
  useEffect(() => {
    if (!name) return
    const before = document.title
    document.title = `${name} · ${APP_NAME}`
    return () => {
      document.title = before
    }
  }, [name])
}

/** The top of every page: title, an optional line, the main action, then the
 *  filters. The attention strip follows it on every page. */
export function PageHeader({ title, description, actions, parent, crumb, children, docTitle, attention = true, attentionOmit, className }: PageHeaderProps) {
  useRegisterHeader()
  const trail = parent ? (Array.isArray(parent) ? parent : [parent]) : []
  useDocumentTitle(docTitle ?? (typeof title === 'string' ? title : undefined))
  const inShell = !!useShell()
  return (
    <>
      <header className={cn('mb-4 space-y-3', className)}>
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            {trail.length > 0 && (
              <nav aria-label="위치" className="mb-1">
                <ol className="flex min-w-0 flex-wrap items-center gap-x-0.5 text-meta text-muted-foreground">
                  {trail.map((c) => (
                    <li key={c.to} className="inline-flex items-center gap-0.5">
                      <Link to={c.to} className="hover:text-foreground">
                        {c.label}
                      </Link>
                      <ChevronRight size={12} aria-hidden />
                    </li>
                  ))}
                  {crumb && (
                    <li aria-current="page" className="min-w-0 max-w-[16rem] truncate text-foreground/80">
                      {crumb}
                    </li>
                  )}
                </ol>
              </nav>
            )}
            <h1 className="text-title font-semibold">{title}</h1>
            {description && <p className="mt-1 text-body text-muted-foreground">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </div>
        {children}
      </header>
      {inShell && attention && <AttentionStrip omit={attentionOmit} className="mb-4" />}
    </>
  )
}
